import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Fade } from '@mui/material';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { auth } from '../utils/auth';

function Login({ setUser }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(true);

  // Check for messages passed from other pages (like signup success)
  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from history to avoid showing it again on navigation
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear general error when typing in any field
    if (errors.general) {
      setErrors(prev => ({
        ...prev,
        general: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: '',
      general: ''
    };
    let isValid = true;

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setSuccess('');
      setErrors({
        email: '',
        password: '',
        general: ''
      });
      
      const user = await auth.login(formData.email, formData.password);
      
      if (user) {
        if (setUser) setUser(user);
        setSuccess('Login successful!');
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle different error cases
      if (err.message.includes('Invalid login credentials') || 
          err.message.includes('Invalid email or password')) {
        setErrors(prev => ({ 
          ...prev, 
          general: 'Invalid email or password. Please try again.' 
        }));
        // Clear password field on auth error
        setFormData(prev => ({ ...prev, password: '' }));
      } else if (err.message.includes('Email not confirmed')) {
        setErrors(prev => ({ 
          ...prev, 
          general: 'Please verify your email address before logging in.' 
        }));
      } else {
        setErrors(prev => ({ 
          ...prev, 
          general: err.message || 'An error occurred during login. Please try again.' 
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Fade in={visible} timeout={1000}>
        <Paper 
          elevation={3} 
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: 3,
            background: theme.palette.background.paper
          }}
        >
          <Typography 
            variant="h3" 
            gutterBottom 
            textAlign="center"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 2
            }}
          >
            Welcome Back
          </Typography>
          <Typography 
            color="text.secondary" 
            textAlign="center" 
            sx={{ mb: 4 }}
          >
            Sign in to continue your learning journey
          </Typography>

          <Alert severity="warning" sx={{ mb: 3 }}>
            This feature is currently disabled. Login functionality is temporarily unavailable.
          </Alert>

          {errors.general && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {errors.general}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {success}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Email Address"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
                autoFocus
                error={!!errors.email}
                helperText={errors.email}
                disabled={true}
                sx={{
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary
                  },
                  '& .MuiFormHelperText-root': {
                    color: theme.palette.error.main
                  }
                }}
              />
              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="current-password"
                error={!!errors.password}
                helperText={errors.password}
                disabled={true}
                sx={{
                  '& .MuiInputBase-input': {
                    color: theme.palette.text.primary
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary
                  },
                  '& .MuiFormHelperText-root': {
                    color: theme.palette.error.main
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        disabled={true}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={true}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{
                  py: 1.5,
                  mt: 2,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                  '&:hover': {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                  }
                }}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                By signing in, you agree to our <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</Link> and <Link to="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</Link>.
              </Typography>
            </Stack>
          </form>

          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 2 }}>
              <Typography color="text.secondary" fontSize="0.875rem">
                Don't have an account?
              </Typography>
            </Divider>
            <Box sx={{ textAlign: 'center' }}>
              <Button 
                component={Link} 
                to="/signup" 
                variant="outlined"
                sx={{ minWidth: 120 }}
              >
                Sign Up
              </Button>
            </Box>
          </Box>
          
          
          

          {/* Test Account Section */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Test Account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: nr7isgoat@proton.me
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Password: Test1234
            </Typography>
          </Box>
        </Paper>
      </Fade>
    </Container>
  );
}

export default Login;
